"use client";
import React, { forwardRef } from "react";
import clsx from "clsx";

export type CardVariant = "outlined" | "filled" | "inverse";
export type CardPadding = "none" | "sm" | "md";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: keyof JSX.IntrinsicElements;
  variant?: CardVariant;
  padding?: CardPadding;
  isInteractive?: boolean;
  children?: React.ReactNode;
  customFilledBgColor?: string;
}

const paddingMap: Record<CardPadding, string> = {
  none: "p-0",
  sm: "p-4",
  md: "p-6"
};

const variantStyles: Record<CardVariant, string> = {
  outlined: "bg-white border border-[#e6e6e6]",
  filled: "border",
  inverse: "bg-gray-900 text-white border-0"
};

const interactiveStyles = `
  cursor-pointer text-left transition-all duration-200 ease-in-out w-full
  hover:bg-gray-50 hover:border-gray-300
  active:scale-[0.98]
`;
// tailwind parity with hubble via claude test
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      as: Component = "div",
      variant = "outlined",
      padding = "md",
      isInteractive = false,
      children,
      className,
      customFilledBgColor,
      style,
      ...props
    },
    ref
  ) => {
    const isButton = Component === "button" || Component === "a";
    const shouldBeInteractive = isInteractive || isButton;

    const classes = clsx(
      "block rounded-lg overflow-hidden relative",
      paddingMap[padding],
      variantStyles[variant],
      shouldBeInteractive && interactiveStyles,
      className
    );

    const elementStyle = {
      ...style,
      ...(variant === "filled" && customFilledBgColor
        ? {
            backgroundColor: customFilledBgColor,
            borderColor: customFilledBgColor
          }
        : {})
    };

    return React.createElement(
      Component,
      {
        ref,
        className: classes,
        style: elementStyle,
        ...props
      },
      variant === "inverse" ? (
        <div className="[&_*]:text-white">{children}</div>
      ) : (
        children
      )
    );
  }
);

Card.displayName = "Card";
